import { apiService } from './apiService';
import { deviceManager } from './deviceManager';

class VoiceService {
  constructor() {
    this.isRecording = false;
    this.mediaRecorder = null;
    this.audioStream = null;
    this.audioChunks = [];
    this.vadWorker = null;
    this.wakeWordDetector = null;
  }

  async initialize() {
    try {
      // Initialize VAD (Voice Activity Detection)
      await this.initializeVAD();
      
      // Initialize wake word detection
      await this.initializeWakeWordDetection();
      
      console.log('Voice service initialized');
      return true;
    } catch (error) {
      console.error('Failed to initialize voice service:', error);
      return false;
    }
  }

  async initializeVAD() {
    // Implement VAD using Web Audio API or external library
    // This is a placeholder for VAD implementation
    console.log('VAD initialized');
  }

  async initializeWakeWordDetection() {
    // Implement wake word detection
    // This could use a library like Picovoice Porcupine
    console.log('Wake word detection initialized');
  }

  async requestMicrophonePermission() {
    try {
      // Use DeviceManager to get microphone access
      const stream = await deviceManager.getAudioStream();
      
      // Stop the stream immediately, we just needed permission
      // Note: DeviceManager will manage the stream lifecycle
      // stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  }

  async startBackgroundListening() {
    // ⚠️ PRESERVE BACKGROUND LISTENING: Don't start if already recording
    if (this.isRecording) {
      console.log('🔄 Background listening already active - preserving for wake word detection');
      return;
    }

    try {
      console.log('🎤 Starting background listening for wake word detection and vector storage');
      
      // ⚠️ AUDIO STREAM SHARING: Use deviceManager instead of globalAudioManager
      // Ensure deviceManager is ready
      if (!deviceManager.isInitialized) {
        await deviceManager.initialize();
      }
      
      // ⚠️ MEDIASTREAM SHARING: Get shared stream from deviceManager
      this.audioStream = deviceManager.getAudioStream();
      console.log('✅ Using shared audio stream from deviceManager for voice service');

      this.mediaRecorder = new MediaRecorder(this.audioStream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(this.audioChunks, {
          type: 'audio/webm;codecs=opus'
        });
        
        // ⚠️ PRESERVE FOR WAKE WORD: Process audio chunk for wake word detection and future vector storage
        await this.processAudioChunk(audioBlob);
      };

      // Record in small chunks for real-time processing (wake word detection)
      this.mediaRecorder.start(1000); // 1 second chunks for responsive wake word detection
      this.isRecording = true;

      console.log('✅ Background listening started - ready for wake word detection and vector storage');
    } catch (error) {
      console.error('❌ Failed to start background listening:', error);
      throw error;
    }
  }

  async stopBackgroundListening() {
    if (!this.isRecording) {
      console.log('🔄 Background listening already stopped');
      return;
    }

    console.log('🛑 Stopping background listening (preserving capability for restart)');
    
    // ⚠️ MEDIASTREAM LEAK FIX: Properly stop MediaRecorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }

    // ⚠️ AUDIO STREAM SHARING: Don't stop shared stream, let deviceManager manage it
    if (this.audioStream) {
      console.log('🔄 Releasing reference to shared audio stream (keeping stream active for other components)');
      this.audioStream = null; // Release reference but don't stop the shared stream
    }

    this.isRecording = false;
    console.log('✅ Background listening stopped - ready for restart when needed');
  }

  async processAudioChunk(audioBlob) {
    try {
      // Send audio to server for processing
      const result = await apiService.processAudio(audioBlob);
      
      if (result.wakeWordDetected) {
        this.handleWakeWordDetected(result);
      } else if (result.shouldStore) {
        console.log('Audio stored for context:', result);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to process audio chunk:', error);
    }
  }

  handleWakeWordDetected(result) {
    console.log('Wake word detected:', result);
    
    // Trigger active listening mode
    this.startActiveListening();
    
    // Notify UI
    window.dispatchEvent(new CustomEvent('wakeWordDetected', {
      detail: result
    }));
  }

  async startActiveListening() {
    // Switch to active listening mode for user queries
    console.log('Active listening mode activated');
    
    // This would implement a different recording strategy
    // for capturing user queries after wake word detection
  }

  async stopActiveListening() {
    console.log('Active listening mode deactivated');
  }

  async testMicrophone() {
    try {
      console.log('🎤 Starting microphone test');
      
      // ⚠️ AUDIO STREAM SHARING: Use deviceManager for testing instead of creating separate stream
      let stream = null;
      let audioContext = null;
      
      try {
        // Use shared stream from deviceManager
        if (!deviceManager.isInitialized) {
          await deviceManager.initialize();
        }
        
        stream = deviceManager.getAudioStream();
        audioContext = deviceManager.getAudioContext();
        const analyser = deviceManager.getAnalyser();
        
        if (!analyser) {
          throw new Error('Analyser not available');
        }
        
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        return new Promise((resolve) => {
          const checkAudio = () => {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / bufferLength;
            
            if (average > 10) { // Threshold for audio detection
              console.log('✅ Microphone test successful - audio detected');
              resolve(true);
            } else {
              setTimeout(checkAudio, 100);
            }
          };
          
          checkAudio();
          
          // Timeout after 5 seconds
          setTimeout(() => {
            console.log('⚠️ Microphone test timeout - no audio detected');
            resolve(false);
          }, 5000);
        });
      } finally {
        // ⚠️ AUDIO STREAM SHARING: Don't close shared resources
        console.log('🔄 Microphone test completed - shared resources remain active');
      }
    } catch (error) {
      console.error('❌ Microphone test failed:', error);
      return false;
    }
  }

  async getAudioDevices() {
    try {
      // Use DeviceManager to get device information
      if (!deviceManager.isInitialized) {
        await deviceManager.initialize();
      }
      
      return {
        microphones: deviceManager.getAvailableInputDevices(),
        speakers: deviceManager.getAvailableOutputDevices()
      };
    } catch (error) {
      console.error('Failed to get audio devices:', error);
      return { microphones: [], speakers: [] };
    }
  }

  cleanup() {
    console.log('🧹 Starting VoiceService cleanup - preserving wake word detection capability');
    
    // ⚠️ PRESERVE BACKGROUND LISTENING: Stop current session but preserve capability
    this.stopBackgroundListening();
    
    // ⚠️ MEMORY LEAK FIX: Properly terminate workers
    if (this.vadWorker) {
      console.log('🛑 Terminating VAD worker');
      this.vadWorker.terminate();
      this.vadWorker = null;
    }
    
    if (this.wakeWordDetector) {
      console.log('🛑 Cleaning up wake word detector');
      // Cleanup wake word detector resources
      this.wakeWordDetector = null;
    }
    
    // Clear audio chunks array
    this.audioChunks = [];
    
    console.log('✅ VoiceService cleanup complete - ready for background listening restart');
  }
}

export const voiceService = new VoiceService();
